// TimelinePage — dual-lane chronological timeline (model | estimate).
//
// Data: GET /estimates/timeline?projectId=
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Skeleton, Empty, message, Card, Tag } from "antd";
import { url, tokenRef } from "api";
import GlobalFilterBar, { useGlobalFilter } from "components/GlobalFilterBar";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";

function auth() { return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` }; }

async function fetchTimeline(projectId) {
  const res = await fetch(`${url}/estimates/timeline?projectId=${projectId}`, { headers: auth() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
async function fetchProjects() {
  const res = await fetch(`${url}/projects/picker`, { headers: auth() });
  if (!res.ok) return [];
  const body = await res.json();
  return Array.isArray(body) ? body : (body?.projects || []);
}

function shortDate(ts) {
  return new Date(ts).toLocaleDateString(undefined, { day: "2-digit", month: "short" }).toUpperCase();
}
function timeOfDay(ts) {
  return new Date(ts).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function ModelCard({ e }) {
  const p = e.payload;
  return (
    <Card size="small" style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontWeight: 600 }}>{p.revisionLabel || "Model commit"}</span>
        <span style={{ color: "#888", fontSize: 12 }}>{e.user?.name} {e.user?.lastName}</span>
      </div>
      <div style={{ fontSize: 13 }}>
        {p.added ? <Tag color="green">+{p.added}</Tag> : null}
        {p.removed ? <Tag color="red">−{p.removed}</Tag> : null}
        {p.modified ? <Tag color="orange">~{p.modified}</Tag> : null}
        {!p.added && !p.removed && !p.modified && (
          <span style={{ color: "#888" }}>No element delta recorded</span>
        )}
        {typeof p.sizeBytes === "number" && p.sizeBytes > 0 && (
          <span style={{ color: "#888", marginLeft: 12 }}>
            {(p.sizeBytes / 1_000_000).toFixed(1)} MB
          </span>
        )}
      </div>
    </Card>
  );
}

function EstimateCard({ e }) {
  const p = e.payload;
  const sym = p.currencySymbol || "$";
  const dirColor = p.direction === "increase" ? "green"
                 : p.direction === "decrease" ? "red" : "default";
  return (
    <Card size="small" style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontWeight: 600 }}>
          {p.revisionLabel ? `${p.revisionLabel} — ` : ""}{sym}{p.totalCost.toLocaleString()}
        </span>
        <span style={{ color: "#888", fontSize: 12 }}>{e.user?.name} {e.user?.lastName}</span>
      </div>
      <div style={{ fontSize: 13 }}>
        {p.deltaFromPrev == null
          ? <Tag>Baseline</Tag>
          : (
            <>
              <Tag color={dirColor}>
                {p.deltaFromPrev > 0 ? "+" : ""}{sym}{Math.abs(p.deltaFromPrev).toLocaleString()}
                {p.percentChange != null && (
                  <> ({p.percentChange > 0 ? "+" : ""}{p.percentChange.toFixed(1)}%)</>
                )}
              </Tag>
              {p.topCategory && <span style={{ color: "#666" }}> · top: {p.topCategory}</span>}
            </>
          )}
      </div>
    </Card>
  );
}

// Exported as a section so DiffAnalyticsPage can offer the dual-lane
// timeline as a fourth layout (lossless merge — the standalone
// /timeline route keeps rendering the same component via the default
// export wrapper at the bottom of this file).
export function DualLaneTimeline({ embedded = false }) {
  const history = useHistory();
  const { filter, onChange, onReset } = useGlobalFilter();

  const [projects, setProjects] = useState([]);
  // Shared coordination memory: ?projectId= wins, else the last project
  // picked anywhere in the suite, else the first project (below).
  const [projectId, setProjectId] = useLastProject(projects);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProjects().then(ps => {
      setProjects(ps);
      if ((!projectId || !ps.some((p) => p._id === projectId)) && ps[0]?._id) setProjectId(ps[0]._id);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    fetchTimeline(projectId)
      .then(d => {
        // Client-side userId filter — backend doesn't filter timeline
        // events by user, so do it here.
        if (filter.userIds?.length > 0 && d?.events) {
          d.events = d.events.filter(e => {
            const uid = String(e.user?._id || e.user || "");
            return filter.userIds.includes(uid);
          });
        }
        setData(d);
      })
      .catch(e => message.error(e.message))
      .finally(() => setLoading(false));
    const sp = new URLSearchParams(window.location.search);
    sp.set("projectId", projectId);
    history.replace(`${window.location.pathname}?${sp.toString()}`);
  }, [projectId, filter.userIds, history]);

  return (
    <div style={{ padding: embedded ? 0 : 24, fontFamily: "Inter, system-ui, sans-serif", maxWidth: 1100 }}>
      {!embedded && <CoordinationBreadcrumbs current="Project Timeline" />}
      <div style={{ marginBottom: 12 }}>
        {!embedded && <h2 style={{ margin: 0 }}>Project Timeline</h2>}
        <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
          Model uploads on the left, estimate revisions on the right.
        </div>
      </div>

      <GlobalFilterBar
        project={projects.find(p => p._id === projectId)}
        projects={projects}
        users={data?.events
          ? Array.from(new Map(data.events.filter(e => e.user).map(e => [String(e.user._id), e.user])).values())
          : []}
        value={filter} onChange={onChange} onReset={onReset}
        onProjectChange={setProjectId}
        counters={{}}
      />

      {loading && <Skeleton active paragraph={{ rows: 6 }} />}

      {!loading && data && data.events.length === 0 && (
        <Empty description="No model commits or estimate revisions yet" />
      )}

      {!loading && data && data.events.length > 0 && (
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 80px 1fr", gap: 0,
        }}>
          {data.events.map((e, i) => (
            <React.Fragment key={i}>
              <div style={{ paddingRight: 12 }}>
                {e.kind === "model" ? <ModelCard e={e} /> : null}
              </div>
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                paddingTop: 12, color: "#666",
              }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{shortDate(e.ts)}</span>
                <span style={{ fontSize: 11, color: "#888" }}>{timeOfDay(e.ts)}</span>
                <div style={{
                  width: 12, height: 12, borderRadius: "50%",
                  background: e.kind === "model" ? "#1F2937" : "#3B82F6",
                  marginTop: 6,
                }} />
                <div style={{ width: 2, flex: 1, background: "#E2E2E2", marginTop: 2, minHeight: 16 }} />
              </div>
              <div style={{ paddingLeft: 12 }}>
                {e.kind === "estimate" ? <EstimateCard e={e} /> : null}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TimelinePage() {
  return <DualLaneTimeline />;
}
